<template>
    <div class="payment-modal">
        <form @submit.prevent="handleSubmit" class="stripe-form">
            <p class="text-zinc-950 font-inter text-lg font-semibold">Add Payment Method</p>
            <div class="divider"></div>
            <div>
                <div class="name-container">
                    <div class="name-div">
                        <label>First name</label>
                        <prime-input-text
                            placeholder="John"
                            :invalid="submitted && !first_name.trim()"
                            v-model="first_name"
                            @keydown="letterOnlyInputHandler"
                            @paste="letterOnlyInputHandler"
                        />
                        <div v-if="submitted && !first_name.trim()" class="validation-message">
                            <span>First name is required.</span>
                        </div>
                    </div>
                    <div class="name-div">
                        <label>Last name</label>
                        <prime-input-text
                            placeholder="Doe"
                            :invalid="submitted && !last_name.trim()"
                            v-model="last_name"
                            @keydown="letterOnlyInputHandler"
                            @paste="letterOnlyInputHandler"
                        />
                        <div v-if="submitted && !last_name.trim()" class="validation-message">
                            <span>Last name is required.</span>
                        </div>
                    </div>
                </div>
                <div id="card-element"></div>
            </div>
            <div class="flex justify-end mt-4 gap-2">
                <PrimeButton label="Cancel" @click="closeDialog" variant="secondary" class="p-btn-root_secondary" />
                <PrimeButton label="Add" type="submit" :loading="paymentLoader" :disabled="!canSubmit" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import PrimeInputText from 'primevue/inputtext';
import { onMounted, ref, computed } from 'vue';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.STRIPE_PAYMENT);
const closeDialog = () => {
    dialogRef.value.close();
};

const { letterOnlyInputHandler } = useUtils();
const { showSuccess, showError, showWarning } = useGlobalToast();
const config = useRuntimeConfig();
const paymentLoader = ref(false);

const stripe = ref();
const elements = ref();
const first_name = ref('');
const last_name = ref('');
const submitted = ref(false);
const isCardComplete = ref(false);

const canSubmit = computed(() => first_name.value.trim() !== '' && last_name.value.trim() !== '' && isCardComplete.value);

onMounted(async () => {
    setupStripe()
});

const setupStripe = async () => {
    const stripeModule = await import('@stripe/stripe-js');
        stripe.value = await stripeModule.loadStripe(config.public.pb.stripeKey);
        const options = {
            mode: "setup",
            currency: "usd",
            paymentMethodCreation: "manual",
            paymentMethodTypes: ["card"]

        };
        elements.value = stripe.value.elements(options);
        const card = elements.value.create("payment", {
            fields: {
                billingDetails: "auto",
            },
            wallets: { googlePay: "never", applePay: "never", link: "never" }
        });
        card.mount('#card-element');

        card.on("change", (event: any) => {
            isCardComplete.value = event.complete;
        });
};

const handleSubmit = async () => {
    if (paymentLoader.value) return;

    submitted.value = true;

    if (!first_name.value || !last_name.value) {
        return;
    }

    paymentLoader.value = true;
    await elements.value.submit();

    const { error, paymentMethod } = await stripe.value.createPaymentMethod({
        elements: elements.value,
        params: {
            billing_details: {
                name: first_name.value + ' ' + last_name.value,
            }
        },
    });

    if (error) {
        paymentLoader.value = false;
        showError('Error', `Payment error: ${error.message}`);
        return;
    }

    const countryCode = paymentMethod?.billing_details?.address?.country;
    if (countryCode === 'RU' || countryCode === 'UA' || countryCode === 'BY') {
        showError(
            'Unsupported Country',
            'Payments from this country are currently restricted under international regulations.'
        );
        paymentLoader.value = false;
        return;
    }

    if (paymentMethod.id) {
        paymentLoader.value = false;
        // Call Nuxt hook with the payment method ID
        const nuxtApp = useNuxtApp();
        await nuxtApp.callHook('payment_method:success', { paymentMethodId: paymentMethod.id });
        closeDialog();
    } else {
        paymentLoader.value = false;
    }
};
</script>
<style lang="scss" scoped>
.card-element {
    @apply w-full;
}

.payment-modal {
    @apply sm:w-full w-11/12 mx-auto lg:max-w-2xl rounded-lg bg-white p-8;
}

.name-div {
    @apply flex flex-col mb-3 w-full;

    label {
        @apply text-zinc-800 text-xs mb-1;
    }
}

.main-footer {
    @apply flex flex-col justify-center items-end pt-8;
}

.divider {
    @apply h-px self-stretch bg-zinc-300 my-5;
}

.actions-div {
    @apply flex justify-end items-center gap-3 px-6 pb-6;
}

.name-container {
    @apply flex flex-col lg:flex-row gap-1 lg:gap-4 w-full;
}
.stripe-form{
    @apply w-full;
}
.validation-message {
    @apply flex gap-1 mt-1;

    span {
        @apply text-red-600 font-inter text-sm font-normal leading-5;
    }
}
</style>
